// Filename: DataWarehousesRequestsPage.tsx
import React, { useState, useEffect } from "react";
import {
    Box,
    Container,
    Typography,
    Divider,
    TextField,
    Autocomplete,
    Chip,
    InputAdornment,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
} from "@mui/material";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";

import queryGraphql from "../../../helpers/ApiInterface";
import MainDrawer from "../../MainDrawer";
import MiniBot from "../../MiniBot";
import DataWarehousesRequestsTable from "./DataWarehousesRequestsTable";

// --- Interface Definitions ---
interface SearchOptions {
    chipLabel: string;
    dropdownLabel: string;
    category: string;
    groupBy: string;
}

interface ProcessedDataWarehouseJob {
    id: string;
    warehouseName: string;
    applicationName?: string;
    status: string;
    createdAt: string | null;
    actionedAt: string | null;
    ownerName: string | null | undefined;
    ownerAvatar?: string;
    approverName: string | null | undefined;
    approverAvatar?: string;
    reasonForRejection?: string;
}

const DataWarehousesRequestsPage: React.FC = () => {
    const [jobs, setJobs] = useState<ProcessedDataWarehouseJob[]>([]);
    const [searchOptions, setSearchOptions] = useState<SearchOptions[]>([]);
    const [searchQuery, setSearchQuery] = useState<SearchOptions[]>([]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await queryGraphql(`
                    query GetDataWarehouses {
                      getDataWarehouses {
                        id
                        name
                        application { id applicationName }
                        status
                        reasonForRejection
                        createdAt
                        updatedAt
                        solutionOwner { id name googleProfilePicture }
                        solutionApprover { id name googleProfilePicture }
                      }
                    }
                `);

                const rawJobsData = data.getDataWarehouses || [];
                
                const processedJobsArr: ProcessedDataWarehouseJob[] = rawJobsData.map((job: any) => ({
                    id: job.id,
                    warehouseName: job.name,
                    applicationName: job.application?.applicationName,
                    status: job.status ? job.status.toLowerCase() : 'pending',
                    createdAt: job.createdAt ? new Date(Number(job.createdAt)).toISOString() : null,
                    actionedAt: job.updatedAt ? new Date(Number(job.updatedAt)).toISOString() : null,
                    ownerName: job.solutionOwner?.name,
                    ownerAvatar: job.solutionOwner?.googleProfilePicture, 
                    approverName: job.solutionApprover?.name,
                    approverAvatar: job.solutionApprover?.googleProfilePicture,
                    reasonForRejection: job.reasonForRejection,
                }));
                setJobs(processedJobsArr);
                
                let generatedOptions: SearchOptions[] = processedJobsArr
                    .map((job: ProcessedDataWarehouseJob) => {
                        const options: SearchOptions[] = [
                            { category: "warehouseName", groupBy: "WAREHOUSE NAME", chipLabel: `Warehouse: ${job.warehouseName}`, dropdownLabel: job.warehouseName },
                        ];
                        if (job.applicationName) {
                            options.push({ category: "applicationName", groupBy: "APPLICATION", chipLabel: `App: ${job.applicationName}`, dropdownLabel: job.applicationName });
                        }
                        if (job.ownerName) {
                            options.push({ category: "ownerName", groupBy: "OWNER", chipLabel: `Owner: ${job.ownerName}`, dropdownLabel: job.ownerName });
                        }
                        if (job.approverName) {
                            options.push({ category: "approverName", groupBy: "APPROVER", chipLabel: `Approver: ${job.approverName}`, dropdownLabel: job.approverName });
                        }
                        if (job.status) {
                            const statusLabel = job.status.charAt(0).toUpperCase() + job.status.slice(1);
                            options.push({ category: "status", groupBy: "STATUS", chipLabel: `Status: ${statusLabel}`, dropdownLabel: statusLabel });
                        }
                        return options;
                    })
                    .flat();

                generatedOptions = generatedOptions.filter(
                    (obj, index, self) => obj.dropdownLabel && index === self.findIndex((t) => t.category === obj.category && t.dropdownLabel === obj.dropdownLabel)
                );

                generatedOptions.sort((a, b) => a.groupBy.localeCompare(b.groupBy) || a.dropdownLabel.localeCompare(b.dropdownLabel));
                setSearchOptions(generatedOptions);

            } catch (error) {
                console.log("Error fetching data warehouses for request page:", error);
            }
        };
        fetchData();
    }, []);

    const filteredJobs = searchQuery.length === 0 ? jobs : jobs.filter((job) => 
        searchQuery.every(query => {
            const jobValue = job[query.category as keyof ProcessedDataWarehouseJob] as string | undefined;
            return jobValue?.toLowerCase().includes(query.dropdownLabel.toLowerCase());
        })
    );

    const pendingStatuses = ['pending', 'submitted', 'provisioning'];
    const approvedStatuses = ['active', 'approved', 'success'];
    const rejectedStatuses = ['rejected', 'failed'];

    const filteredPendingJobs = filteredJobs.filter(job => pendingStatuses.includes(job.status));
    const filteredApprovedJobs = filteredJobs.filter(job => approvedStatuses.includes(job.status));
    const filteredRejectedJobs = filteredJobs.filter(job => rejectedStatuses.includes(job.status));

    return (
        <Box sx={{ display: "flex" }}>
            <MainDrawer index={"requests-data-warehouses"} />
            <MiniBot />
            <Box component="main" sx={{ flex: 1, bgcolor: "#fafafa", minHeight: "100vh" }}>
                <Container maxWidth={false} disableGutters>
                    <Box bgcolor="#FFFFFF" p={2} mb="18px">
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", my: 1, mx: 2 }}>
                            <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold">Requests</Typography>
                             <IconButton aria-label="Help" onClick={()=> window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs` , "_blank")} sx={{ p: 0, border: 'none', mr:2, '&:hover': { backgroundColor: 'action.hover' }}}>
                                <Box component="img" src="/assets/Help.svg" alt="Help Icon" sx={{ width: 40, height: 40, display: 'block' }} />
                            </IconButton>
                        </Box>
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mx: 2 }}>
                            <Box>
                                <Typography fontFamily="Gilroy-SemiBold" fontSize="16px">{`DATA WAREHOUSES (${jobs.length})`}</Typography>
                                <Typography fontFamily="Gilroy-Regular" fontSize="14px">{`View all the requests raised for Data Warehouses`}</Typography>
                            </Box>
                            <Autocomplete
                                multiple options={searchOptions} filterSelectedOptions getOptionLabel={(option) => option.dropdownLabel}
                                value={searchQuery} groupBy={(option) => option.groupBy}
                                renderTags={(tagValue, getTagProps) => tagValue.map((option, index) => <Chip {...getTagProps({ index })} size="small" label={option.chipLabel} />)}
                                onChange={(e, newVal) => { setSearchQuery(newVal); }}
                                renderInput={(params) => (
                                    <TextField {...params} variant="filled" placeholder="Search by warehouse, owner, status etc"
                                        fullWidth size="small" hiddenLabel
                                        InputProps={{...params.InputProps,
                                            startAdornment: ( <> <InputAdornment position="start"> <img src="/assets/search-normal.svg" alt="Search Icon" style={{ width: "16px", paddingLeft: "4px" }} /> </InputAdornment> {params.InputProps.startAdornment} </> ),
                                            disableUnderline: true,
                                            style: { backgroundColor: "#f4f4f4", minHeight: "45px", width: "500px", borderRadius: "8px", fontFamily: "Gilroy-Regular", fontSize: "14px" }
                                        }}
                                    />
                                )}
                            />
                        </Box>
                    </Box>
                    <Box m={2}>
                        <Accordion defaultExpanded={filteredPendingJobs.length > 0} elevation={0} sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }} disabled={filteredPendingJobs.length === 0}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}><Box sx={{ display: "flex", alignItems: "center" }}><img src="/assets/clock.svg" alt="Pending Icon" style={{ width: "20px", marginRight: "8px" }}/><Typography fontSize="16px" fontFamily="Gilroy-SemiBold">Pending Warehouses({filteredPendingJobs.length})</Typography></Box></AccordionSummary>
                            <AccordionDetails>{filteredPendingJobs.length > 0 && (<DataWarehousesRequestsTable jobs={filteredPendingJobs} />)}</AccordionDetails>
                        </Accordion>
                        
                        <Accordion defaultExpanded={true} elevation={0} sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }} disabled={filteredApprovedJobs.length === 0}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}><Box sx={{ display: "flex", alignItems: "center" }}><img src="/assets/tickcircle.svg" alt="Approved Icon" style={{ width: "20px", marginRight: "8px" }} /><Typography fontSize="16px" fontFamily="Gilroy-SemiBold">Approved Warehouses({filteredApprovedJobs.length})</Typography></Box></AccordionSummary>
                            <AccordionDetails>{filteredApprovedJobs.length > 0 && (<DataWarehousesRequestsTable jobs={filteredApprovedJobs} />)}</AccordionDetails>
                        </Accordion>

                        <Accordion defaultExpanded={filteredRejectedJobs.length > 0} elevation={0} sx={{ borderRadius: "8px", "&:before": { display: "none" }, bgcolor: "#fafafa" }} disabled={filteredRejectedJobs.length === 0}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}><Box sx={{ display: "flex", alignItems: "center" }}><img src="/assets/reject.svg" alt="Rejected Icon" style={{ width: "20px", marginRight: "8px" }} /><Typography fontSize="16px" fontFamily="Gilroy-SemiBold">Rejected Warehouses({filteredRejectedJobs.length})</Typography></Box></AccordionSummary>
                            <AccordionDetails>{filteredRejectedJobs.length > 0 && (<DataWarehousesRequestsTable jobs={filteredRejectedJobs} />)}</AccordionDetails>
                        </Accordion>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default DataWarehousesRequestsPage;